import React, { useEffect } from 'react'

import { Button, TextInput } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import i18n from 'Pods/Shared/I18n'

import MTCaptcha from 'Utils/MTCaptcha'

import { EMAIL_REGEX } from '../AuthStep/AuthStep'
import PasswordField from './Components/PasswordField/PasswordField'

const CAPTCHA_ID = 'auth-modal-login-captcha'

function PasswordStep({
  email,
  password,
  isSubmitting,
  isTwoFaRequired,
  twoFaCode,
  captchaEnabled,
  passwordError = '',
  showRequiredNotice = false,
  locale = 'en',
  onEmailChange,
  onPasswordChange,
  onTwoFaCodeChange,
  onSubmit,
  onBack,
}) {
  const t = key => i18n.t(`auth_modal.password.${key}`, { ns: 'rails', lng: locale })
  const canSubmit = isTwoFaRequired
    ? Boolean(twoFaCode.trim())
    : EMAIL_REGEX.test(email.trim()) && password.length > 0

  // The switch link keeps DOM focus on the auth step's node, so move focus to
  // the field the user came here to fill - mirroring AuthStep's switch-mode.
  useEffect(() => {
    const fieldId = isTwoFaRequired ? 'sign-in-two-fa-code' : 'sign-in-password'
    document.getElementById(fieldId)?.focus()
  }, [isTwoFaRequired])

  const handleSubmit = (event) => {
    event.preventDefault()
    if (canSubmit && !isSubmitting) {
      onSubmit()
    }
  }

  return (
    <form className="auth-modal__password" onSubmit={handleSubmit} noValidate>
      <div className="auth-modal__header-group auth-modal__header-group--center">
        <p className="auth-modal__title cgt-heading-300">
          {isTwoFaRequired
            ? i18n.t('login.two_fa.title', { ns: 'rails', lng: locale })
            : t('title')}
        </p>
        <p className="auth-modal__subtitle cgt-body-md">
          {isTwoFaRequired
            ? i18n.t('login.two_fa.description', { ns: 'rails', lng: locale })
            : t('subtitle')}
        </p>
      </div>
      {!isTwoFaRequired && showRequiredNotice && (
        <p className="auth-modal__password-notice cgt-body-base">{t('required_notice')}</p>
      )}
      {isTwoFaRequired ? (
        <TextInput
          id="sign-in-two-fa-code"
          name="twoFaCode"
          size="medium"
          label={t('two_fa_code_label')}
          inputMode="numeric"
          autoComplete="one-time-code"
          value={twoFaCode}
          onChange={onTwoFaCodeChange}
          wrapperClassName="auth-modal__field"
          isPilot
        />
      ) : (
        <>
          <TextInput
            id="sign-in-password-email"
            name="email"
            inputType="email"
            size="medium"
            label={i18n.t('auth_modal.email_label', { ns: 'rails', lng: locale })}
            value={email}
            onChange={onEmailChange}
            wrapperClassName="auth-modal__field"
            autoComplete="username"
            isPilot
          />
          <PasswordField value={password} locale={locale} onChange={onPasswordChange} />
          <p className="auth-modal__forgot-password">
            <a
              href="/users/password/new"
              className="auth-modal__switch-link cgt-body-md-semibold cgt-link-primary-accented"
            >
              {t('forgot_password')}
            </a>
          </p>
        </>
      )}
      {captchaEnabled && <MTCaptcha captchaId={CAPTCHA_ID} />}
      <Button
        actionType="submit"
        size="medium"
        disabled={!canSubmit || isSubmitting}
        customClass="auth-modal__submit"
      >
        {isTwoFaRequired
          ? i18n.t('login.two_fa.verify_button', { ns: 'rails', lng: locale })
          : t('submit_button')}
      </Button>
      {passwordError && (
        <p className="auth-modal__password-error cgt-body-base" aria-live="polite">
          {passwordError}
        </p>
      )}
      <p className="auth-modal__back">
        <button
          type="button"
          className="auth-modal__switch-link cgt-body-md-semibold cgt-link-primary-accented"
          onClick={onBack}
        >
          {t('back')}
        </button>
      </p>
    </form>
  )
}

PasswordStep.propTypes = {
  email: string.isRequired,
  password: string.isRequired,
  isSubmitting: bool.isRequired,
  isTwoFaRequired: bool.isRequired,
  twoFaCode: string.isRequired,
  captchaEnabled: bool.isRequired,
  passwordError: string,
  showRequiredNotice: bool,
  locale: string,
  onEmailChange: func.isRequired,
  onPasswordChange: func.isRequired,
  onTwoFaCodeChange: func.isRequired,
  onSubmit: func.isRequired,
  onBack: func.isRequired,
}

export default PasswordStep
